// Data for connection
import axios, {AxiosError} from "axios";
import {Wrapped, wrapStatus} from "./wrapped";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import {Config, JwtResponse, WaError} from "../types/config";
import {Claim, ClaimResult, Friend, Task, UserContactsPatch, WaUser} from "../types/farming";
import {JettonRate, JettonWallet, RawMetadata, UserRate} from "../types/catalog";
import {
    AdminChannel, AdminSubscriptionPlan, ChannelUser,
    Subscription,
    UserChannel,
    UserSubscriptionPlan
} from "../types/subscriptions";
import {Resources, Wallet} from "../types/explyt";


export let serverAddress = "http://127.0.0.1:8080";
export let frontendAddress = "https://explyt.github.io";
export let webappAddress = "t.me/explytbot/app";
let token = ""
let resetAuthLayer: () => Promise<void> = () => Promise.resolve()

export function getImageUrl(filename: string) {
    return `${serverAddress}/media/images/${filename}`;
}

export function setToken(j: string) {
    token = j;
}

export function setResetAuthLayer(target: () => Promise<void>) {
    resetAuthLayer = target;
}

if (process.env.NODE_ENV === "production") {
    serverAddress = `https://get.explyt.dev`
    frontendAddress = "https://explyt.github.io"
    webappAddress = "t.me/explytbot/app"
}
if (process.env.REACT_APP_STAGE === "stage") {
    serverAddress = `https://devbot.esprito.com`
    frontendAddress = "https://devbot.esprito.com"
    webappAddress = "t.me/somenew_verytest_explyt_bot/app"
}

export const SENDING = -2;
export const NOT_SENT = -1;
export const LOADING = 0;
export const FETCH_ERROR = 1;
export const LOADED = 200;
export const AUTH_FAILED = 401;
export const NOT_FOUND = 404;


// Function that will be called to refresh authorization
const refreshAuthLogic = (failedRequest: AxiosError) => {
    if (failedRequest.response!.status !== AUTH_FAILED) {
        return Promise.reject(failedRequest)
    }
    return resetAuthLayer().then(() => {
        failedRequest.response!.config.headers['Authorization'] = 'Bearer ' + token;
        return Promise.resolve();
    })
};

// Instantiate the interceptor
createAuthRefreshInterceptor(axios, refreshAuthLogic);


export function postAuth(payload: string): Promise<Wrapped<JwtResponse>> {
    const init = {
        method: 'POST',
        headers: {"Content-Type": "application/json"},
        body: payload
    }
    return wrapStatus(fetch(`${serverAddress}/explyt-api/auth`, init));
}


export function postUsers(payload: string): Promise<Wrapped<WaUser>> {
    const init = {
        method: 'POST',
        headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
        body: payload
    }
    return wrapStatus(fetch(`${serverAddress}/explyt-api/users`, init));
}

function fetchSigned<T>(url: string, method: string, body: string | null = null): Promise<Wrapped<T>> {
    const headers: any = {
        "Content-Type": "application/json",
        "Access-Control-Allow-Headers": "*"
    };
    headers["Authorization"] = `Bearer ${token}`;
    const req = axios(url, {
        method: method,
        headers: headers,
        data: body
    });

    return req.then((res : any) => {
        return {statusCode: res.status, data: res.data, errorMessage: null}
    }).catch((err: AxiosError) => {
        if (err.response === undefined)
            return {
                statusCode: FETCH_ERROR,
                data: null,
                errorMessage: {code: "UNKNOWN", message: err.message}
            };
        return {
            statusCode: err.response.status === undefined ? FETCH_ERROR : err.response.status,
            data: null,
            errorMessage: (err.response?.data as WaError)
        };
    });
}

export function postClaim(): Promise<Wrapped<ClaimResult>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/claim`, "POST");
}

export function patchPress(data: string): Promise<Wrapped<any>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me`, "PATCH", data);
}

export function getTasks(): Promise<Wrapped<Task[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/tasks`, "GET");
}

export function patchTask(taskId: number, body: string): Promise<Wrapped<any>> {
    return fetchSigned(
        `${serverAddress}/explyt-api/users/me/tasks/${taskId}`,
        "PATCH",
        body);
}

export function taskClaim(): Promise<Wrapped<ClaimResult>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/claimTasks`, "POST");
}

export function getUser(): Promise<Wrapped<WaUser>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me`, "GET");
}

export function getFriends(): Promise<Wrapped<Friend[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/referrals`, "GET");
}

export function postClaimFriends(): Promise<Wrapped<ClaimResult>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/claim?type=referrals`, "POST");
}

export function getJettonWallets(walletId: string): Promise<Wrapped<JettonWallet[]>> {
    return wrapStatus(fetch(`https://toncenter.com/api/v3/jetton/wallets?owner_address=${walletId}&limit=256&offset=0`));
}

export function patchAddress(data: string): Promise<Wrapped<any>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me`, "PATCH", data);
}

export function getConfig(): Promise<Wrapped<Config>> {
    return fetchSigned(`${serverAddress}/explyt-api/configs`, "GET");
}

export function getJettonMetadata(address: string): Promise<Wrapped<{ source: string, metadata: RawMetadata }>> {
    return wrapStatus(fetch(`https://api.ton.cat/v2/contracts/jetton_minter/${address}/raw_metadata`));
}

export function getMyRate(address: string): Promise<Wrapped<UserRate>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/rates/${address}`, "GET");
}

export function postMyRate(address: string, rate: UserRate): Promise<Wrapped<ClaimResult>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/rates/${address}`, "POST", JSON.stringify(rate));
}

export function getJettonRate(address: string): Promise<Wrapped<JettonRate>> {
    return fetchSigned(`${serverAddress}/explyt-api/rates/${address}`, "GET");
}

export function getChannels(): Promise<Wrapped<UserChannel[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/channels`, "GET");
}

export function getSubscriptions(): Promise<Wrapped<Subscription[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/subscriptions`, "GET");
}

export function getSubscription(channelId: string): Promise<Wrapped<Subscription>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/subscriptions/${channelId}`, "GET");
}

export function stopSubscription(channelId: string): Promise<Wrapped<Subscription>> {
    const body = {
        "auto_prolongation": false,
    }
    return fetchSigned(`${serverAddress}/explyt-api/users/me/subscriptions/${channelId}`, "PATCH", JSON.stringify(body));
}

export function stopSubscriberSubscription(userId: number, channelId: string): Promise<Wrapped<Subscription>> {
    const body = {
        "auto_prolongation": false,
    }
    return fetchSigned(`${serverAddress}/owner/explyt-api/users/${userId}/subscriptions/${channelId}`, "PATCH", JSON.stringify(body));
}

export function restartSubscription(channelId: string): Promise<Wrapped<Subscription>> {
    const body = {
        "auto_prolongation": true,
    }
    return fetchSigned(`${serverAddress}/explyt-api/users/me/subscriptions/${channelId}`, "PATCH", JSON.stringify(body));
}

export function getPlanWithPromo(channelId: string, planId: string, promoCode: string): Promise<Wrapped<UserSubscriptionPlan>> {
    return fetchSigned(`${serverAddress}/explyt-api/channels/${channelId}/plans/${planId}?promo=${promoCode}`, "GET");
}

export function postPlan(channelId: string, plan: AdminSubscriptionPlan): Promise<Wrapped<AdminSubscriptionPlan>> {
    return fetchSigned(`${serverAddress}/explyt-api/owner/channels/${channelId}/plans`, "POST", JSON.stringify(plan));
}

export function putPlan(channelId: string, plan: AdminSubscriptionPlan): Promise<Wrapped<AdminSubscriptionPlan>> {
    return fetchSigned(`${serverAddress}/explyt-api/owner/channels/${channelId}/plans/${plan.plan_id}`, "PUT", JSON.stringify(plan));
}

export function postSubscribe(channelId: string, planId: string, currency: "Credits", promo: string | null): Promise<Wrapped<ClaimResult>> {
    const data = {
        "channel_id": channelId,
        "plan_id": planId,
        "currency": currency,
        "promo": promo,
    }
    return fetchSigned(`${serverAddress}/explyt-api/users/me/subscriptions`, "POST", JSON.stringify(data));
}

export function getSubscribers(channelId: string) : Promise<Wrapped<ChannelUser[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/owner/channels/${channelId}/subscribers`, "GET");
}

export function getAdminChannel(channelId: string) : Promise<Wrapped<AdminChannel>> {
    // TODO: Change URL
    return fetchSigned(`${serverAddress}/explyt-api/owner/channels/${channelId}`, "GET");
}

export function putChannel(channelId: string, channel : AdminChannel) : Promise<Wrapped<any>> {
    const body = JSON.stringify(channel);
    return fetchSigned(`${serverAddress}/explyt-api/owner/channels/${channelId}`, "PUT", body);
}

export function patchUserContacts(patch: UserContactsPatch) : Promise<Wrapped<WaUser>> {
    const body = JSON.stringify(patch);
    return fetchSigned(`${serverAddress}/explyt-api/users/me`, "PATCH", body);
}

export function getResources() : Promise<Wrapped<Resources>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/resources`, "GET");
}

export function postGenerateAPIKey() : Promise<Wrapped<Resources>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/resources/explytApiToken`, "POST");
}

export function getWallets() : Promise<Wrapped<Wallet[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/wallets`, "GET");
}

export function getTransactions() : Promise<Wrapped<Claim[]>> {
    return fetchSigned(`${serverAddress}/explyt-api/users/me/transactions`, "GET");
}

export function postWithdraw(id : string | null, username: string | null, amount: number) : Promise<Wrapped<ClaimResult>> {
    const body =  JSON.stringify({
        wa_id: id,
        username: username,
        amount: amount,
        payment_type: "Esprito"
    });
    return fetchSigned(`${serverAddress}/explyt-api/users/me/withdraw`, "POST", body)
}
